/** TONIGHT'S CHAT — the operator's moderation, on Live Game View.
 *
 *  Shows the room as it stands, including what has already been deleted, and
 *  gives staff two actions: delete a message from the players' screens, and
 *  mute a player for tonight. Neither touches the 7-day audit, which is the
 *  super admin's own record in Audit Logs.
 *
 *  The card appears only where "Live chat" is on.
 */
import { useCallback, useEffect, useState } from 'react';
import { MessageSquare, Trash2, VolumeX, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const EVERY = 8000;

export const ChatModeration = ({ orgId, neo = {}, t }) => {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!orgId) return;
        try {
            const res = await api.get('/live-game/chat',
                { ...neo, params: { organization_id: orgId } });
            setData(res.data);
        } catch (_e) { /* a dropped read changes nothing on screen */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    useEffect(() => {
        load();
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') load();
        }, EVERY);
        return () => clearInterval(id);
    }, [load]);

    const act = async (path, payload) => {
        setBusy(true);
        try {
            const res = await api.post(`/live-game/chat/${path}`,
                { organization_id: orgId, ...payload }, neo);
            setData(res.data);
        } catch (_e) {
            toast.error(t('liveGame.chatFailed', 'Could not do that'));
        } finally { setBusy(false); }
    };

    if (!data?.enabled) return null;
    const messages = data.messages || [];
    const silenced = new Set(data.silenced || []);

    return (
        <Card className="w-full" data-testid="live-game-chat-moderation">
            <CardContent className="p-4 space-y-3">
                <p className="flex items-center gap-2 text-sm font-semibold">
                    <MessageSquare className="w-4 h-4 text-sky-500" />
                    {t('liveGame.chatModerationTitle', 'Tonight’s chat')}
                </p>

                {messages.length === 0 ? (
                    <p className="text-xs text-muted-foreground" data-testid="live-game-chat-empty">
                        {t('liveGame.chatModerationEmpty', 'No messages tonight.')}
                    </p>
                ) : (
                    <div className="space-y-2" data-testid="live-game-chat-rows">
                        {messages.map((m) => (
                            <div key={m.id} className="rounded-lg border border-border p-2.5"
                                 data-testid="live-game-chat-row">
                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                    <span className="text-sm font-semibold">{m.player}</span>
                                    <span className="text-[11px] text-muted-foreground tabular-nums">
                                        {new Date(m.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                                <p className="text-[11px] text-muted-foreground">
                                    {m.establishment || t('goalGame.chatOffLoc', 'Off loc')}
                                </p>
                                <p className="mt-1 break-words text-sm">{m.text}</p>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <Button size="sm" variant="outline" disabled={busy}
                                            onClick={() => act('delete', { message_id: m.id })}
                                            data-testid="live-game-chat-delete">
                                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                        {t('liveGame.chatDelete', 'Delete')}
                                    </Button>
                                    <Button size="sm" variant="outline" disabled={busy}
                                            onClick={() => act('mute', { player_key: m.key,
                                                                         on: !silenced.has(m.key) })}
                                            data-testid="live-game-chat-mute">
                                        {silenced.has(m.key)
                                            ? <Volume2 className="w-3.5 h-3.5 mr-1.5" />
                                            : <VolumeX className="w-3.5 h-3.5 mr-1.5" />}
                                        {silenced.has(m.key)
                                            ? t('liveGame.chatUnmuteTonight', 'Un-mute')
                                            : t('liveGame.chatMuteTonight', 'Mute tonight')}
                                    </Button>
                                    {m.reported && (
                                        <span className="text-[11px] font-semibold text-amber-600"
                                              data-testid="live-game-chat-reported">
                                            {t('goalGame.chatReported', 'Reported')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default ChatModeration;
